import { useEnvironment, type AccountEnvironment } from "@keycloak/keycloak-account-ui";
import {
    Nav,
    NavExpandable,
    NavItem,
    NavList,
    PageSidebar,
    PageSidebarBody,
    Spinner,
} from "@patternfly/react-core";
import {
    PropsWithChildren,
    MouseEvent as ReactMouseEvent,
    Suspense,
    useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import {
    matchPath,
    useHref,
    useLinkClickHandler,
    useLocation,
} from "react-router-dom";
import { routes as menuItems, MenuItem } from "../routes";

export const PageNav = () => {
    const context = useEnvironment<AccountEnvironment>();

    return (
        <PageSidebar>
            <PageSidebarBody>
                <Nav>
                    <NavList>
                        <Suspense fallback={<Spinner />}>
                            {menuItems
                                .filter((menuItem) =>
                                    menuItem.isVisible
                                        ? context.environment.features[menuItem.isVisible]
                                        : true,
                                )
                                .map((menuItem) => (
                                    <NavMenuItem
                                        key={menuItem.label}
                                        menuItem={menuItem}
                                    />
                                ))}
                        </Suspense>
                    </NavList>
                </Nav>
            </PageSidebarBody>
        </PageSidebar>
    );
};

type NavMenuItemProps = {
    menuItem: MenuItem;
};

function NavMenuItem({ menuItem }: NavMenuItemProps) {
    const { t } = useTranslation();
    const { environment } = useEnvironment<AccountEnvironment>();
    const { pathname } = useLocation();
    const isActive = useMemo(
        () => matchMenuItem(pathname, menuItem, environment.baseUrl),
        [pathname, menuItem],
    );

    if ("path" in menuItem) {
        return (
            <NavLink path={menuItem.path} isActive={isActive}>
                {t(menuItem.label)}
            </NavLink>
        );
    }

    return (
        <NavExpandable
            data-testid={menuItem.label}
            title={t(menuItem.label)}
            isActive={isActive}
            isExpanded={isActive}
        >
            {menuItem.children
                .filter((menuItem) =>
                    menuItem.isVisible ? environment.features[menuItem.isVisible] : true,
                )
                .map((child) => (
                    <NavMenuItem key={child.label} menuItem={child} />
                ))}
        </NavExpandable>
    );
}

function getFullUrl(path: string, baseUrl: string) {
    return `${new URL(baseUrl).pathname}${path}`;
}

function matchMenuItem(currentPath: string, menuItem: MenuItem, baseUrl: string): boolean {
    if ("path" in menuItem) {
        return !!matchPath(getFullUrl(menuItem.path, baseUrl), currentPath);
    }

    return menuItem.children.some((child) => matchMenuItem(currentPath, child, baseUrl));
}

type NavLinkProps = {
    path: string;
    isActive: boolean;
};

export function NavLink ({
    path,
    isActive,
    children,
}: PropsWithChildren<NavLinkProps>) {
    const { environment } = useEnvironment<AccountEnvironment>();
    const menuItemPath = getFullUrl(path, environment.baseUrl) + location.search;
    const href = useHref(menuItemPath);
    const handleClick = useLinkClickHandler(menuItemPath);

    return (
        <NavItem
            data-testid={path}
            to={href}
            isActive={isActive}
            // PatternFly does not have the correct type for this event, so we need to cast it.
            onClick={(event: unknown) =>
                handleClick(event as ReactMouseEvent<HTMLAnchorElement>)
            }
        >
            {children}
        </NavItem>
    );
}
